import { FC } from 'react';
import dayjs from 'dayjs';

import { useSelector } from '../../../redux/store';
import { selectMyApprovedGroups, selectMyDeniedGroups } from '../../../redux/State/clientSlice/scheduleCancellationSelectors';
import { GREEN, RED } from '../../../shared/Helpers/colors';
import StatusChip, { StatusChipItem, StatusChipPopoverList } from './StatusChip';

export const useVisibleMonthFilter = <T extends { startDate: string; endDate: string }>(items: T[]): T[] => {
	const visibleMonth = useSelector((state) => state.calendarHelper.month);
	const visibleYear = useSelector((state) => state.calendarHelper.year);
	const visibleStart = new Date(visibleYear, visibleMonth, 1);
	const visibleEnd = new Date(visibleYear, visibleMonth + 1, 0, 23, 59, 59, 999);
	return items.filter(({ startDate, endDate }) =>
		new Date(startDate) <= visibleEnd && new Date(endDate) >= visibleStart,
	);
};

const CONFIG = {
	approved: {
		color: GREEN,
		statusLabel: 'Approved',
		chipLabel: (n: number) => `approved request${n === 1 ? '' : 's'}`,
		heading: 'Approved Time Off',
	},
	denied: {
		color: RED,
		statusLabel: 'Denied',
		chipLabel: (n: number) => `denied request${n === 1 ? '' : 's'}`,
		heading: 'Denied Time Off',
	},
} as const;

interface Props {
	variant: 'approved' | 'denied';
}

const MyCancellationStatusChip: FC<Props> = ({ variant }) => {
	const approvedGroups = useSelector(selectMyApprovedGroups);
	const deniedGroups = useSelector(selectMyDeniedGroups);
	const allGroups = variant === 'approved' ? approvedGroups : deniedGroups;
	const groups = useVisibleMonthFilter(allGroups);
	const { color, statusLabel, chipLabel, heading } = CONFIG[variant];

	if (groups.length === 0) return null;

	const items: StatusChipItem[] = groups.map((g) => ({
		key: g.key,
		title: g.reasonName,
		subtitle: `${dayjs(g.startDate).format('MMM D')} \u2013 ${dayjs(g.endDate).format('MMM D')} \u2022 ${statusLabel}`,
		dotColor: color.BASE,
		extra: g.decisionNotes ? `Note: ${g.decisionNotes}` : undefined,
	}));

	return (
		<StatusChip
			count={groups.length}
			label={chipLabel(groups.length)}
			colorScheme={{ background: color.QUATERNARY, text: color.DARK[16], border: color.LIGHT[48] }}
			popoverContent={<StatusChipPopoverList heading={heading} items={items} />}
		/>
	);
};

export default MyCancellationStatusChip;
